// Dragging a Fleet Live panel to a zone by its grip (#6779, prototype `:3649-3684`). The grip
// is POINTER-ONLY; "Place in" is the keyboard path to the same move.
//
// A drag starts after 6 px of pointer travel. While it runs, every zone shows its outline and
// label (the section reads `active()`), the zone under the pointer highlights (`over()`), a
// ghost follows the pointer (`ghost()`), and the page scrolls 12 px per move within 70 px of
// the viewport's edge. Esc cancels; a drop on a zone moves the panel to the end of it.
//
// LISTENERS LIVE ONLY AS LONG AS A DRAG: `pointermove` / `pointerup` / `pointercancel` and
// Esc are added on the grip's `pointerdown` and removed when it ends, so an idle section
// holds no window listener.

import { createSignal, onCleanup } from 'solid-js';
import type { LiveZone } from './fleet-live-layout';
import type { FleetLivePanel } from './fleet-live-vis';

/** Pointer travel (|dx| + |dy|) before a press becomes a drag. */
export const DRAG_START_PX = 6;
/** Within this far of the viewport's top or bottom edge, a drag scrolls the page. */
export const EDGE_PX = 70;
export const EDGE_SCROLL_PX = 12;

export type PanelDrag = {
  /** The grip's `pointerdown`: arm a drag of `panel`. */
  readonly press: (panel: FleetLivePanel, e: PointerEvent) => void;
  /** The panel being dragged, once past the threshold; `null` otherwise. */
  readonly active: () => FleetLivePanel | null;
  /** The zone under the pointer while dragging. */
  readonly over: () => LiveZone | null;
  /** Where the ghost sits, in viewport px. */
  readonly ghost: () => { readonly x: number; readonly y: number } | null;
};

export type PanelDragDeps = {
  /** The zone at a viewport point, or `null` (read off the section's own zone elements). */
  readonly zoneAt: (x: number, y: number) => LiveZone | null;
  /** The drag started, or crossed into a zone. */
  readonly onLift: () => void;
  readonly onDrop: (panel: FleetLivePanel, zone: LiveZone) => void;
};

export function createPanelDrag(deps: PanelDragDeps): PanelDrag {
  const [active, setActive] = createSignal<FleetLivePanel | null>(null);
  const [over, setOver] = createSignal<LiveZone | null>(null);
  const [ghost, setGhost] = createSignal<{ x: number; y: number } | null>(null);
  let armed: { panel: FleetLivePanel; x0: number; y0: number; id: number } | null = null;

  const end = (drop: boolean): void => {
    const was = armed;
    const panel = active();
    const zone = over();
    armed = null;
    globalThis.removeEventListener('pointermove', move);
    globalThis.removeEventListener('pointerup', up);
    globalThis.removeEventListener('pointercancel', cancel);
    globalThis.removeEventListener('keydown', key);
    if (globalThis.document !== undefined) globalThis.document.body.style.userSelect = '';
    setActive(null);
    setOver(null);
    setGhost(null);
    if (drop && was !== null && panel !== null && zone !== null) deps.onDrop(panel, zone);
  };

  function move(e: PointerEvent): void {
    if (armed === null || e.pointerId !== armed.id) return;
    if (active() === null) {
      if (Math.abs(e.clientX - armed.x0) + Math.abs(e.clientY - armed.y0) < DRAG_START_PX) return;
      setActive(armed.panel);
      globalThis.document.body.style.userSelect = 'none';
      deps.onLift();
    }
    setGhost({ x: e.clientX, y: e.clientY });
    const zone = deps.zoneAt(e.clientX, e.clientY);
    if (zone !== over()) {
      setOver(zone);
      if (zone !== null) deps.onLift();
    }
    const h = globalThis.innerHeight;
    if (e.clientY < EDGE_PX) globalThis.scrollBy?.(0, -EDGE_SCROLL_PX);
    else if (e.clientY > h - EDGE_PX) globalThis.scrollBy?.(0, EDGE_SCROLL_PX);
  }
  function up(e: PointerEvent): void {
    if (armed === null || e.pointerId !== armed.id) return;
    end(active() !== null);
  }
  function cancel(): void {
    end(false);
  }
  function key(e: KeyboardEvent): void {
    if (e.key === 'Escape') end(false);
  }

  onCleanup(() => end(false));

  return {
    press(panel, e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      end(false);
      armed = { panel, x0: e.clientX, y0: e.clientY, id: e.pointerId };
      globalThis.addEventListener('pointermove', move);
      globalThis.addEventListener('pointerup', up);
      globalThis.addEventListener('pointercancel', cancel);
      globalThis.addEventListener('keydown', key);
    },
    active,
    over,
    ghost,
  };
}
